// The live load monitor (#32, ADR 0003): turns the voice's telemetry into load
// samples for the quality controller (quality-controller.ts).
//
// The worklet has no fine clock, so a single block's time in process() reads 0
// or 1 ms; but every batch header carries totals since the processor started
// (process ms, frames, stalls), so the load over a window is exact to about
// 0.1 % however many records or batches were lost (docs/contracts/audio-bridge.md
// § Load and deadline misses). The monitor keeps one snapshot of those totals
// per batch and, every `intervalS` of audio, reports the load over the last
// `windowS` (about a second, as ADR 0003 says) and over the short window since
// its previous sample, with the deadline misses since then:
// AudioContext.playbackStats underruns where the browser has them (Chrome 146
// and later; it updates them about once a second), otherwise the processor's
// stalls.
//
// Chrome's CPU throttling (CDP) slows the main thread but never the audio
// thread (#6), so throttling cannot fake a slow computer; the voice's own
// busy-work control, debug.extra_cost, does (web/dev/bench.html).

import type { AudioEngine } from "../engine";
import { R_ENTRY_MS, type TelemetryBatch } from "../protocol";

/** The load over one window of audio. Times are audio seconds since the processor started. */
export interface LoadSample {
  /** Where the window starts and ends. */
  start: number;
  end: number;
  /** Share of the window's audio time spent in process(), 0 to 1 and beyond when the thread falls behind. */
  load: number;
  /** Where the short window since the previous sample starts (about `intervalS` before `end`). */
  recentStart: number;
  /** The load over that short window, for a sudden overload. */
  recentLoad: number;
  /** Deadline misses since the previous sample: `underruns` where the browser reports them, else `stalls`. */
  misses: number;
  /** playbackStats underruns since the previous sample, or null where the browser has none. */
  underruns: number | null;
  /** Gaps between process() calls longer than the stall threshold, since the previous sample. */
  stalls: number;
  /** The longest gap between two process() calls since the previous sample, ms. */
  maxGapMs: number;
  /** Wall-clock time minus audio time over the window, ms; it grows while the audio falls behind. */
  driftMs: number;
}

export interface LoadMonitorOptions {
  /** Length of the window the load is averaged over, s of audio (default 1). */
  windowS?: number;
  /** How often a sample is reported, s of audio (default 0.25). */
  intervalS?: number;
}

/** The totals of one batch header. */
interface Snapshot {
  frames: number;
  processMs: number;
  entryMs: number;
  stalls: number;
  underruns: number | null;
}

/** playbackStats.underrunEvents of a context, where the browser reports it. */
export function underrunsOf(context: BaseAudioContext): number | null {
  const stats = (context as { playbackStats?: { underrunEvents?: unknown } }).playbackStats;
  return typeof stats?.underrunEvents === "number" ? stats.underrunEvents : null;
}

export class LoadMonitor {
  readonly windowS: number;
  readonly intervalS: number;
  private snaps: Snapshot[] = [];
  private previous: Snapshot | null = null;
  private maxGap = 0;

  constructor(options: LoadMonitorOptions = {}) {
    this.windowS = options.windowS ?? 1;
    this.intervalS = options.intervalS ?? 0.25;
  }

  /** Forgets everything, for a new processor (its totals start again from 0). */
  reset(): void {
    this.snaps = [];
    this.previous = null;
    this.maxGap = 0;
  }

  /**
   * Takes one telemetry batch, with the context's underrun count read now (or
   * null). Returns a sample when one is due, else null.
   */
  addBatch(batch: TelemetryBatch, underruns: number | null): LoadSample | null {
    if (batch.count === 0) return null;
    const rate = batch.sampleRate;
    const snap: Snapshot = {
      frames: batch.framesTotal,
      processMs: batch.processMsTotal,
      entryMs: batch.get(batch.count - 1, R_ENTRY_MS),
      stalls: batch.stallsTotal,
      underruns,
    };
    const newest = this.snaps[this.snaps.length - 1];
    if (newest !== undefined && snap.frames < newest.frames) this.reset(); // another processor
    this.maxGap = Math.max(this.maxGap, batch.maxGapMs);
    this.snaps.push(snap);
    const windowFrames = this.windowS * rate;
    // Keep the newest snapshot that is at least a window old, and everything after it.
    while (this.snaps.length > 2 && snap.frames - this.snaps[1]!.frames >= windowFrames) this.snaps.shift();
    const first = this.snaps[0]!;
    const audioMs = ((snap.frames - first.frames) / rate) * 1000;
    if (audioMs < this.windowS * 1000) return null;
    const prev = this.previous;
    if (prev !== null && snap.frames - prev.frames < this.intervalS * rate) return null;

    // The first sample counts the misses over its whole window.
    const since = prev ?? first;
    const stalls = snap.stalls - since.stalls;
    const underrunDelta =
      snap.underruns === null ? null : since.underruns === null ? 0 : snap.underruns - since.underruns;
    const recentMs = ((snap.frames - since.frames) / rate) * 1000;
    const sample: LoadSample = {
      start: first.frames / rate,
      end: snap.frames / rate,
      load: (snap.processMs - first.processMs) / audioMs,
      recentStart: since.frames / rate,
      recentLoad: (snap.processMs - since.processMs) / recentMs,
      misses: underrunDelta ?? stalls,
      underruns: underrunDelta,
      stalls,
      maxGapMs: this.maxGap,
      driftMs: snap.entryMs - first.entryMs - audioMs,
    };
    this.previous = snap;
    this.maxGap = 0;
    return sample;
  }

  /** Feeds every telemetry batch of `engine` into the monitor and hands each sample to `listener`. */
  attach(engine: AudioEngine, listener: (sample: LoadSample) => void): () => void {
    return engine.onTelemetry((batch) => {
      const sample = this.addBatch(batch, underrunsOf(engine.context));
      if (sample !== null) listener(sample);
    });
  }
}
